import React, { useId } from "react";
import styled from "styled-components";

import { theme } from "../styles/theme";

const SPHERE_STROKE = 1.5;

/** Size of the mockup's initials, as a share of the sphere's diameter. */
const INITIALS_RATIO = 0.42;

interface GlassAvatarProps {
  initials: string;
  size?: number;
  className?: string;
}

/**
 * Initials on a glass sphere, the same avatar the mobile redesign uses.
 *
 * Drawn in SVG because a single flat border cannot be bright over the top-left quarter, gone across
 * the middle and bright again at the bottom-right. The fill runs on the same diagonal: fully
 * transparent at the top-left, so the panel's own blue comes through untouched, and gathering light
 * towards the bottom-right.
 */
export function GlassAvatar({ initials, size = 44, className }: Readonly<GlassAvatarProps>) {
  // Gradient ids live in the SVG document, so they are suffixed to survive two avatars on a page.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const center = size / 2;

  return (
    <Container className={className} $size={size}>
      <Sphere width={size} height={size} aria-hidden="true">
        <defs>
          {/* Stays empty until the last quarter, so the glow hugs the rim instead of flooding the
              lower half of the sphere. */}
          <linearGradient id={`glassFill${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="0.75" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0.24" />
          </linearGradient>
          {/* Full white held over the first stretch then dropped fast, which is what makes the
              top-left arc read as a crisp edge; the 0.04 floor keeps the silhouette barely there
              on the other diagonal instead of closing a visible ring. */}
          <linearGradient id={`glassRim${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="0.15" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="0.25" stopColor="#ffffff" stopOpacity="0.15" />
            <stop offset="0.33" stopColor="#ffffff" stopOpacity="0.04" />
            <stop offset="0.67" stopColor="#ffffff" stopOpacity="0.04" />
            <stop offset="0.8" stopColor="#ffffff" stopOpacity="0.15" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0.5" />
          </linearGradient>
        </defs>
        <circle
          cx={center}
          cy={center}
          r={center - SPHERE_STROKE / 2}
          fill={`url(#glassFill${uid})`}
          stroke={`url(#glassRim${uid})`}
          strokeWidth={SPHERE_STROKE}
        />
      </Sphere>
      <Initials $fontSize={Math.round(size * INITIALS_RATIO)}>{initials}</Initials>
    </Container>
  );
}

const Container = styled.div<{ $size: number }>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
`;

const Sphere = styled.svg`
  position: absolute;
  inset: 0;
`;

const Initials = styled.span<{ $fontSize: number }>`
  ${theme.boldText};
  position: relative;
  font-size: ${(props) => props.$fontSize}px;
  line-height: 1;
  color: #ffffff;
`;
